import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import LogUtil from './utils/LogUtil';
import { TOAST_COLORS } from './utils/toastColors';
import { HelmetProvider } from 'react-helmet-async';
import { registerNativePush } from './notification/nativePushService';
import { Toaster, toast } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { PollProvider } from './contexts/PollContext';
import { StreamChatProvider } from './messaging/contexts/StreamChatContext';
import { LocationProvider } from './contexts/LocationContext';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { RefreshProvider } from './contexts/RefreshContext';
import PullToRefresh from './components/common/PullToRefresh';
import { initializeGA, applyConsentMode } from './services/analyticsService';
import { Capacitor } from '@capacitor/core';
const isCapacitor = Capacitor.isNativePlatform();
import { useAnalyticsTracking } from './hooks/useAnalyticsTracking';
import Header from './components/common/Header';
import SessionWarningModal from './components/common/SessionWarningModal';
import InAppBrowserBanner from './components/common/InAppBrowserBanner';
import UpdateAvailableModal from './components/common/UpdateAvailableModal';
import Footer from './components/common/Footer';
import MobileNavFooter from './components/common/MobileNavFooter';
import PaymentCheckoutPage from './payments/pages/PaymentCheckoutPage';
import ProtectedRoute from './components/common/ProtectedRoute';
import HomePage from './pages/HomePage';
import Home2Page from './pages/Home2Page';
import LandingV1Page from './pages/LandingV1Page';
import LandingV2Page from './pages/LandingV2Page';
import LandingV3Page from './pages/LandingV3Page';
import SignInPage from './profile/pages/auth/SignInPage';
import SignUpPage from './profile/pages/auth/SignUpPage';
import VerifyEmailPage from './profile/pages/auth/VerifyEmailPage';
import ForgotPasswordPage from './profile/pages/auth/ForgotPasswordPage';
import SocialFeedPage from './social/pages/SocialFeedPage';
import PlayerProfilePage from './profile/pages/PlayerProfilePage';
import TournamentListPage from './tournaments/pages/TournamentListPage';
import CreateTournamentPage from './tournaments/pages/CreateTournamentPage';
import TournamentDetailPage from './tournaments/pages/TournamentDetailPage';
import ExploreTournamentFormatsPage from './tournament_formats/ExploreTournamentFormatsPage';
import TournamentDocumentPage from './tournaments/pages/TournamentDocumentPage';
import TeamDetailPage from './teams/pages/TeamDetailPage';
import CreateTeamPage from './teams/pages/CreateTeamPage';
import ScoringPage from './pages/ScoringPage';
import {
  MarketplacePage,
  ProductDetailPage,
  CheckoutPage,
  SellGearPage,
  ListingDetailPage,
  SavedListingsPage
} from './marketplace';
import ProfileEditPage from './profile/pages/ProfileEditPage';
import SquadDetailsPage from './squads/pages/SquadDetailsPage';
import SquadListPage from './squads/pages/SquadListPage';
import MessagesPage from './messaging/pages/MessagesPage';
import TeammatesPage from './teammates/pages/TeammatesPage';
import InvitationsPage from './invitations/pages/InvitationsPage';
import MyGamesPage from './pages/MyGamesPage';
import PredictionsPage from './pages/PredictionsPage';
import TeamsListPage from './teams/pages/TeamsListPage';
import TeamViewPage from './teams/pages/TeamViewPage';
import PlayerViewPage from './profile/pages/PlayerViewPage';
import SportPage from './pages/SportPage';
import HelpPage from './pages/HelpPage';
import UnsubscribePage from './pages/UnsubscribePage';
import UnsubscribeConfirmPage from './pages/UnsubscribeConfirmPage';
import ScrollToTop from './components/common/ScrollToTop';
import HelpChat from './components/common/HelpChat';
import CookieConsentBanner from './components/common/CookieConsentBanner';
import ContentPolicyModal from './components/common/ContentPolicyModal';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import DataDeletionPage from './pages/DataDeletionPage';
import ContactPage from './pages/ContactPage';
import ErrorBoundary from './components/common/ErrorBoundary';
import NotFoundPage from './pages/NotFoundPage';
import RouteSeo from './components/common/RouteSeo';
import NotificationsPage from './notification/NotificationsPage';
import AcademyListPage from './academy/pages/AcademyListPage';
import AcademyDetailsPage from './academy/pages/AcademyDetailsPage';
import ClassDetailsPage from './academy/pages/ClassDetailsPage';
import CreateEditAcademyPage from './academy/pages/CreateEditAcademyPage';
import ClassRegistrationPage from './academy/pages/ClassRegistrationPage';
import ProgramRegistrationPage from './academy/pages/ProgramRegistrationPage';
import AcademyPortalPage from './academy/pages/AcademyPortalPage';
import GlobalLoadingOverlay from './components/common/GlobalLoadingOverlay';
import StepUpGate from './components/common/StepUpGate';

// Hides Kynaix chrome (header/footer) for portal routes
function PortalLayoutHider({ children }) {
  const { pathname } = useLocation();
  // Checkout joins the portals in going chrome-free: someone part-way through
  // paying should have two ways out, not a nav bar full of them.
  const isPortal = pathname === '/elite' || pathname.startsWith('/academy/')
    || pathname.startsWith('/checkout');
  if (isPortal) return null;
  return <>{children}</>;
}

/**
 * The mobile tab bar survives the academy portal on purpose — dropping it there
 * left a phone with no way out. Checkout is the opposite case: the way out is
 * the point, and it is on the page. A tab bar here is eight ways to abandon a
 * half-finished payment.
 */
function HideOnCheckout({ children }) {
  const { pathname } = useLocation();
  return pathname.startsWith('/checkout') ? null : <>{children}</>;
}

// Handles taps on native push notifications — navigates to the linked URL.
// Must live inside <Router> so useNavigate works.
function NativePushNavigator() {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = (e) => {
      const { url } = e.detail;
      if (url?.startsWith('/')) navigate(url);
    };
    window.addEventListener('nativePushTapped', handler);
    return () => window.removeEventListener('nativePushTapped', handler);
  }, [navigate]);
  return null;
}

// Updates LogUtil page context on every route change — must live inside <Router>
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    LogUtil.setPage(location.pathname);
  }, [location.pathname]);

  // Show linked account toast on first Google sign-in for an existing account
  useEffect(() => {
    const msg = sessionStorage.getItem('linkedAccountMessage');
    if (msg) {
      sessionStorage.removeItem('linkedAccountMessage');
      toast.success(msg, { duration: 6000 });
    }
  }, [location.pathname]);

  return null;
}


const AppContent = () => {
  const { theme, currentTheme } = useTheme();
  const toastColors = TOAST_COLORS[currentTheme] || TOAST_COLORS.light;

  return (
    <RefreshProvider>
    <AuthProvider>
      <PollBridge>
        <LocationProvider>
          <StreamChatProvider>
            <BreadcrumbProvider>
              <Router basename="/" future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <ScrollToTop />
                <RouteSeo />
                <RouteTracker />
                <NativePushNavigator />
                <SessionWarningModal />
                <InAppBrowserBanner />
                <UpdateAvailableModal />
                <div className={`flex flex-col min-h-screen ${theme.background} ${theme.text} transition-colors duration-200`}>
                  <Toaster
                    position="top-center"
                    containerStyle={{
                      top: 'max(16px, env(safe-area-inset-top, 16px))',
                    }}
                    toastOptions={{
                      duration: 3000,
                      style: {
                        borderRadius: '10px',
                        padding: '12px 16px',
                        background: toastColors.bg,
                        color: toastColors.text,
                        border: `1px solid ${toastColors.border}`,
                      },
                    }}
                  />
                  <PortalLayoutHider>
                    <Header />
                  </PortalLayoutHider>

                  <PullToRefresh>
                  <main className="flex-grow md:pb-0" style={{ paddingBottom: 'calc(4rem + env(safe-area-inset-bottom))' }}>
                    <ErrorBoundary>
                      <Routes>
                        {/* Public routes */}
                        <Route path="/" element={<LandingV1Page />} />
                        <Route path="/home2" element={<Home2Page />} />
                        <Route path="/landing-v1" element={<LandingV1Page />} />
                        <Route path="/landing-v2" element={<LandingV2Page />} />
                        <Route path="/landing-v3" element={<LandingV3Page />} />
                        <Route path="/signin" element={<SignInPage />} />
                        <Route path="/signup" element={<SignUpPage />} />
                        <Route path="/claim-account" element={<SignUpPage />} />
                        <Route path="/verify-email" element={<VerifyEmailPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/sport/:sportSlug" element={<SportPage />} />
                        <Route path="/help" element={<HelpPage />} />
                        <Route path="/unsubscribe" element={<UnsubscribePage />} />
                        <Route path="/unsubscribe/confirm" element={<UnsubscribeConfirmPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/terms" element={<TermsOfServicePage />} />
                        <Route path="/privacy" element={<PrivacyPolicyPage />} />
                        <Route path="/cookies" element={<CookiePolicyPage />} />
                        <Route path="/data-deletion" element={<DataDeletionPage />} />

                        {/* Protected routes - require authentication */}
                        <Route path="/social-feed" element={<ProtectedRoute><SocialFeedPage /></ProtectedRoute>} />
                        {/* Chrome-free, and Stripe's own return destination —
                            see PortalLayoutHider and HideOnCheckout above. */}
                        <Route path="/checkout" element={<ProtectedRoute><PaymentCheckoutPage /></ProtectedRoute>} />
                        <Route path="/profile" element={<ProtectedRoute><PlayerProfilePage /></ProtectedRoute>} />
                        <Route path="/profile/edit" element={<ProtectedRoute><ProfileEditPage /></ProtectedRoute>} />
                        <Route path="/profile/teams" element={<ProtectedRoute><TeamsListPage /></ProtectedRoute>} />
                        <Route path="/profile/teams/new" element={<ProtectedRoute><CreateTeamPage /></ProtectedRoute>} />
                        <Route path="/profile/teams/:id" element={<ProtectedRoute><TeamViewPage /></ProtectedRoute>} />
                        <Route path="/profile/teams/:id/edit" element={<ProtectedRoute><CreateTeamPage /></ProtectedRoute>} />
                        <Route path="/tournaments" element={<ProtectedRoute><TournamentListPage /></ProtectedRoute>} />
                        <Route path="/tournaments/explore-formats" element={<ProtectedRoute><ExploreTournamentFormatsPage /></ProtectedRoute>} />
                        <Route path="/tournament/:id" element={<ProtectedRoute><TournamentDetailPage /></ProtectedRoute>} />
                        {/* Same page, mounted under /academy/ so PortalLayoutHider keeps the academy chrome-free */}
                        <Route path="/academy/:slug/tournament/:id" element={<ProtectedRoute><TournamentDetailPage /></ProtectedRoute>} />
                        <Route path="/tournament/:id/terms" element={<ProtectedRoute><TournamentDocumentPage /></ProtectedRoute>} />
                        <Route path="/tournament/:id/waiver" element={<ProtectedRoute><TournamentDocumentPage /></ProtectedRoute>} />
                        <Route path="/team/:teamName" element={<ProtectedRoute><TeamDetailPage /></ProtectedRoute>} />
                        <Route path="/teams" element={<ProtectedRoute><TeamsListPage /></ProtectedRoute>} />
                        <Route path="/teams/new" element={<ProtectedRoute><CreateTeamPage /></ProtectedRoute>} />
                        <Route path="/teams/:id" element={<ProtectedRoute><TeamViewPage /></ProtectedRoute>} />
                        <Route path="/teams/:id/edit" element={<ProtectedRoute><CreateTeamPage /></ProtectedRoute>} />
                        <Route path="/player/:handle" element={<ProtectedRoute><PlayerViewPage /></ProtectedRoute>} />
                        <Route path="/create-tournament" element={<ProtectedRoute><CreateTournamentPage /></ProtectedRoute>} />
                        <Route path="/create-team" element={<ProtectedRoute><CreateTeamPage /></ProtectedRoute>} />
                        <Route path="/edit-team/:id" element={<ProtectedRoute><CreateTeamPage /></ProtectedRoute>} />
                        <Route path="/scoring" element={<ProtectedRoute><ScoringPage /></ProtectedRoute>} />
                        {/* Marketplace routes */}
                        <Route path="/marketplace" element={<MarketplacePage />} />
                        <Route path="/marketplace/listing/new" element={<ProtectedRoute><SellGearPage /></ProtectedRoute>} />
                        <Route path="/marketplace/listing/:id" element={<ListingDetailPage />} />
                        <Route path="/marketplace/listing/:id/edit" element={<ProtectedRoute><SellGearPage /></ProtectedRoute>} />
                        <Route path="/marketplace/saved" element={<ProtectedRoute><SavedListingsPage /></ProtectedRoute>} />
                        {/* New squad routes - main routes */}
                        <Route path="/squads" element={<ProtectedRoute><SquadListPage /></ProtectedRoute>} />
                        <Route path="/squads/:squadId" element={<ProtectedRoute><SquadDetailsPage /></ProtectedRoute>} />
                        {/* Squad sub-routes */}
                        <Route path="/squads/:squadId/tournaments" element={<ProtectedRoute><TournamentListPage /></ProtectedRoute>} />
                        <Route path="/squads/:squadId/teams" element={<ProtectedRoute><TeamsListPage /></ProtectedRoute>} />
                        <Route path="/squads/:squadId/teams/new" element={<ProtectedRoute><CreateTeamPage /></ProtectedRoute>} />
                        <Route path="/squads/:squadId/teams/:id" element={<ProtectedRoute><TeamViewPage /></ProtectedRoute>} />
                        <Route path="/squads/:squadId/teams/:id/edit" element={<ProtectedRoute><CreateTeamPage /></ProtectedRoute>} />
                        <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
                        <Route path="/teammates" element={<ProtectedRoute><TeammatesPage /></ProtectedRoute>} />
                        <Route path="/invitations" element={<ProtectedRoute><InvitationsPage /></ProtectedRoute>} />
                        <Route path="/my-games" element={<ProtectedRoute><MyGamesPage /></ProtectedRoute>} />
                        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
                        <Route path="/elite" element={<AcademyPortalPage />} />
                        <Route path="/academy/:slug" element={<AcademyDetailsPage />} />
                        {/* Academy public routes (no auth required) */}
                        <Route path="/register/class/:classId" element={<ClassRegistrationPage />} />
                        {/* Academy routes — nav entry hidden behind the academiesModule global
                            toggle (Header.jsx), but reachable by direct URL like smartSchedule's
                            routes. Real licensing enforcement happens server-side on creation. */}
                        <Route path="/academies" element={<ProtectedRoute><AcademyListPage /></ProtectedRoute>} />
                        <Route path="/academies/new" element={<ProtectedRoute><CreateEditAcademyPage /></ProtectedRoute>} />
                        <Route path="/academies/:academyId/edit" element={<ProtectedRoute><CreateEditAcademyPage /></ProtectedRoute>} />
                        <Route path="/academies/:academyId/classes/:classId" element={<ProtectedRoute><ClassDetailsPage /></ProtectedRoute>} />
                        {/* Program registration, mounted on both sides. The /academy/:slug
                            form is the one we link to — PortalLayoutHider keeps it inside the
                            academy's own chrome, so the wizard doesn't yank the user out to
                            Kynaix Sports mid-registration. The /academies/ form stays for
                            links already in the wild. */}
                        <Route path="/academy/:slug/programs/:programId/register" element={<ProtectedRoute><ProgramRegistrationPage /></ProtectedRoute>} />
                        <Route path="/academies/:academyId/programs/:programId/register" element={<ProtectedRoute><ProgramRegistrationPage /></ProtectedRoute>} />
                        <Route path="/predictions" element={<ProtectedRoute><PredictionsPage /></ProtectedRoute>} />
                        {/* 404 catch-all */}
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </ErrorBoundary>
                  </main>
                  </PullToRefresh>

                  <PortalLayoutHider>
                    <Footer />
                    <HelpChat />
                    {!isCapacitor && <CookieConsentBanner />}
                    <ContentPolicyModal />
                  </PortalLayoutHider>
                  {/* Outside PortalLayoutHider on purpose. The academy portal
                      drops Kynaix's header and footer so the academy's own
                      branding stands alone, but on a phone that also removed
                      the only way to get anywhere else — you could enter an
                      academy and have no navigation at all. */}
                  <HideOnCheckout><MobileNavFooter /></HideOnCheckout>
                  <GlobalLoadingOverlay />
                  {/* Listens for the server's 428 STEP_UP_REQUIRED and collects
                      the emailed code, so any guarded call anywhere in the app
                      prompts and then completes on its own. */}
                  <StepUpGate />
                </div>
              </Router>
            </BreadcrumbProvider>
          </StreamChatProvider>
        </LocationProvider>
      </PollBridge>
    </AuthProvider>
    </RefreshProvider>
  );
};

// Bridges auth state into PollProvider (must be inside AuthProvider)
const PollBridge = ({ children }) => {
  const { user } = useAuth();
  return <PollProvider isAuthenticated={!!user}>{children}</PollProvider>;
};

function App() {
  // Initialize Google Analytics once when the app mounts
  useEffect(() => {
    initializeGA();
    // On Capacitor (native iOS/Android), cookie consent doesn't apply —
    // auto-grant analytics so GA data flows from the mobile app
    if (isCapacitor) {
      applyConsentMode({ analytics: true, marketing: false, preferences: true });
    }
  }, []);

  // Apply GA consent mode whenever the user updates their cookie preferences (web only)
  useEffect(() => {
    if (isCapacitor) return;
    const handler = (e) => applyConsentMode(e.detail);
    window.addEventListener('kynaix:consent-updated', handler);
    return () => window.removeEventListener('kynaix:consent-updated', handler);
  }, []);

  // Register for native push notifications on iOS/Android
  useEffect(() => {
    if (!isCapacitor) return;
    registerNativePush();
  }, []);

  // Set up centralized analytics tracking for tabs and buttons
  useAnalyticsTracking();

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider>
          <FeatureFlagsProvider>
            <AppContent />
          </FeatureFlagsProvider>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;